import type { RowData } from "@tanstack/react-table";
import { Link } from "@design-system/Link/Link";
import { ArrowCornerIcon, ArrowRightIcon } from "@icons";
import { useCellControlTabIndex } from "./cellControls";
import { toDisplayText } from "./cellValues";
import { EmptyCellValue } from "./EmptyCellValue";
import { SearchableText } from "./SearchableText";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

/**
 * Where a link cell goes: an `href` (opened in a new tab, with an
 * external-arrow affordance) or an in-app action such as opening a side
 * panel (with a chevron affordance). With neither, the value is plain text.
 */
export interface DataGridLinkTarget<TData extends RowData> {
    href?: (row: TData) => string | undefined;
    onActivate?: (row: TData) => void;
}

interface LinkCellProps<TData extends RowData>
    extends DataGridCellProps<TData>, DataGridLinkTarget<TData> {}

/**
 * Link column cell: the value as a design-system `Link` with search
 * highlights. Its affordance arrow shows on row hover, the focused row, and
 * the active cell. Out of the Tab order unless the grid lets its cell tab in;
 * Enter on the active cell follows the link.
 */
export const LinkCell = <TData extends RowData>({
    context,
    meta,
    href,
    onActivate,
}: Readonly<LinkCellProps<TData>>) => {
    const tabIndex = useCellControlTabIndex();
    const label = toDisplayText(context.getValue());
    if (label === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const row = context.row;
    const url = href?.(row.original);
    const text = (
        <SearchableText
            text={label}
            overflow={meta.overflow}
            table={context.table}
        />
    );
    if (url !== undefined) {
        return (
            <Link
                href={url}
                target="_blank"
                rel="noreferrer"
                aria-label={`${label} (opens in new tab)`}
                className={styles.link}
                tabIndex={tabIndex}
                data-dt-activate=""
                rightIcon={
                    <span className={styles.linkAffordance}>
                        <ArrowCornerIcon aria-hidden />
                    </span>
                }
            >
                {text}
            </Link>
        );
    }
    if (!onActivate) {
        return text;
    }
    return (
        <Link
            className={styles.link}
            tabIndex={tabIndex}
            data-dt-activate=""
            onClick={() => onActivate(row.original)}
            rightIcon={
                <span className={styles.linkAffordance}>
                    <ArrowRightIcon aria-hidden />
                </span>
            }
        >
            {text}
        </Link>
    );
};
